import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useMount } from 'react-use';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';

import { useToggle } from '../../../../libs/react-use-extra';
import { audienceSelector } from '../../../../redux/selectors/audience';
import { convertAudienceDataForDraft } from '../../../../Component/_shared/utils';
import {
  resetAudienceBuilder,
  saveStandAloneAudienceName,
  setAudienceData,
} from '../../../../redux/actions/audience';
import { groupsSelector } from '../../../../redux/selectors/tags';
import { fetchContactFullNames } from '../../../../redux/services/contact';
import { fetchOrgTopicsAndGroups } from '../../../../redux/services/groups';
import { selectedOrganizationSelector } from '../../../../redux/selectors/organization';
import { loadGroups } from '../../../../redux/actions/groups';
import { loadTopics } from '../../../../redux/actions/topics';
import Audience from '../../../../Component/Sendmessage/Audience';
import {
  ADD,
  EXCLUDE,
  REQUIRE,
} from '../../../../Component/Sendmessage/Audience/constants';
import { useEditor } from '../CampaignEditor';
import Step from '../Step';
import { useFullScreenSpinner, useToast } from '../../../../redux/actions/UI';
import { fetchCampaign } from '../../api';

// Helper function to check if Redux audience state has any data
const hasAudienceData = audienceState =>
  audienceState.selectedTopicsList.length > 0 ||
  Object.keys(audienceState.selectedGroups).length > 0 ||
  Object.keys(audienceState.selectedMembers).length > 0 ||
  Object.keys(audienceState.selectedFilters).length > 0;

// Validation logic for audience step
export const useAudienceValidation = () => {
  const editor = useEditor();
  const name = editor.getField('name');
  const audience = editor.getField('audience');
  return (
    typeof name === 'string' &&
    name.trim().length > 0 &&
    typeof audience === 'object' &&
    audience !== null
  );
};

const useResetAudienceBuilder = () => {
  const editor = useEditor();
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const campaignIdRef = useRef(null);

  useEffect(() => {
    const currentCampaignId = editor.getField('_id');
    const campaignAudience = editor.getField('audience');

    // Check if we're switching to a different campaign
    const isDifferentCampaign =
      campaignIdRef.current !== null &&
      campaignIdRef.current !== currentCampaignId;

    // Always reset Redux immediately when switching campaigns
    // This ensures old audience is not shown while new one is loading
    if (isDifferentCampaign) {
      dispatch(resetAudienceBuilder());
      campaignIdRef.current = currentCampaignId;
      return;
    }

    // If campaign has no audience, reset Redux to show empty state
    if (!campaignAudience) {
      const state = store.getState();
      const reduxHasData = hasAudienceData(state.audience);
      if (reduxHasData) {
        dispatch(resetAudienceBuilder());
      }
      campaignIdRef.current = currentCampaignId;
      return;
    }

    // If campaign has an audience and it's the same campaign, check if Redux matches
    // Only reset if Redux has different data (preserve state when navigating between steps)
    const state = store.getState();
    const reduxHasData = hasAudienceData(state.audience);

    // If Redux is empty but campaign has audience, don't reset here
    // Let the initialization logic load it
    if (!reduxHasData && campaignAudience) {
      campaignIdRef.current = currentCampaignId;
      return;
    }

    // Track current campaign ID
    campaignIdRef.current = currentCampaignId;
  }, [location.pathname]);
};

const useApplyDefaultAudienceName = () => {
  const editor = useEditor();
  const dispatch = useDispatch();

  return () => {
    const campaignName = editor.getField('name');
    const audienceDefaultName = `${campaignName ?? 'Campaign'} Audience`;
    dispatch(saveStandAloneAudienceName(audienceDefaultName));
  };
};

const useInitializeAudience = () => {
  useResetAudienceBuilder();

  const editor = useEditor();
  const dispatch = useDispatch();
  const store = useStore();
  const applyDefaultAudienceName = useApplyDefaultAudienceName();
  const location = useLocation();

  const selectedOrganization = useSelector(selectedOrganizationSelector);
  const groups = useSelector(groupsSelector);

  const [isLoading, { off: markLoaded }] = useToggle(true);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const isInitializingRef = useRef(false); // Prevent multiple initializations
  const hasInitializedRef = useRef(false); // Track if initialization has completed
  const campaignIdRef = useRef(null); // Track campaign ID to detect switches

  const calcSelectedGroups = (audience, groupsToUse) => {
    const selectedGroups = {};

    const processGroupIds = (groupIds, action) => {
      groupIds?.forEach(groupId => {
        const group = groupsToUse.find(g => g.id === groupId);
        if (group) {
          selectedGroups[groupId] = { name: group.name, action };
        }
      });
    };

    processGroupIds(audience.selectedGroupIds, ADD);
    processGroupIds(audience.excludedGroupIds, EXCLUDE);
    processGroupIds(audience.requiredGroupIds, REQUIRE);

    return selectedGroups;
  };

  const calcSelectedMembers = async audience => {
    const selectedMembers = {};

    const allContactIds = [
      ...(audience.includedMemberIds || []),
      ...(audience.excludedMemberIds || []),
    ];
    const uniqueContactIds = [...new Set(allContactIds)];

    if (uniqueContactIds.length === 0) {
      return selectedMembers;
    }

    const fullNamesByContact = await fetchContactFullNames(uniqueContactIds, {
      organizationId: selectedOrganization.id,
    });

    const processMemberIds = (memberIds, action) => {
      memberIds?.forEach(contactId => {
        const fullName = fullNamesByContact[contactId];
        if (fullName) {
          selectedMembers[contactId] = { name: fullName, action };
        }
      });
    };

    processMemberIds(audience.includedMemberIds, ADD);
    processMemberIds(audience.excludedMemberIds, EXCLUDE);

    return selectedMembers;
  };

  useMount(async () => {
    const currentCampaignId = editor.getField('_id');
    campaignIdRef.current = currentCampaignId;

    const haveAudience = editor.getField('audience');

    if (!haveAudience) {
      setGroupsLoaded(true);
      markLoaded();
      hasInitializedRef.current = true; // Mark as initialized even when there's no audience
      return;
    }

    if (selectedOrganization?.id) {
      try {
        const { tags, topics } = await fetchOrgTopicsAndGroups(
          selectedOrganization.id
        );
        dispatch(loadGroups(tags));
        dispatch(loadTopics(topics));
        // This is a workaround to ensure the state is updated before it is referenced
        await new Promise(resolve => setTimeout(resolve, 0));
        const state = store.getState();
        if ((state.groups || []).length > 0 || tags.length === 0) {
          setGroupsLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load groups and topics:', error);
        if (groups.length > 0) setGroupsLoaded(true);
      }
    } else {
      setGroupsLoaded(true);
    }
  });

  useEffect(() => {
    // Skip if groups aren't loaded yet
    if (!groupsLoaded) return;

    const currentCampaignId = editor.getField('_id');
    const isNewCampaignRoute = location.pathname.includes('/automation/new');

    // Check if we're switching to a different campaign
    const isDifferentCampaign =
      campaignIdRef.current !== null &&
      campaignIdRef.current !== currentCampaignId;

    // Reset initialization flag when switching campaigns or on new campaign route
    // This ensures we reload audience for the new campaign
    if (isDifferentCampaign || isNewCampaignRoute) {
      hasInitializedRef.current = false;
      campaignIdRef.current = currentCampaignId;
      // Redux should already be reset by useResetAudienceBuilder
      // But ensure it's empty before loading new data
      const state = store.getState();
      if (hasAudienceData(state.audience)) {
        dispatch(resetAudienceBuilder());
      }
    }

    // Skip if initialization has already completed for this campaign
    if (hasInitializedRef.current) return;

    const initializeAudience = async () => {
      // Double-check to prevent race conditions
      if (hasInitializedRef.current) return;
      isInitializingRef.current = true;

      try {
        // Always load audience from campaign data
        // Redux should be empty at this point (reset by useResetAudienceBuilder)
        const audience = editor.getField('audience');

        if (
          audience &&
          audience.topicIds &&
          (audience.topicIds.length > 0 ||
            (audience.selectedGroupIds &&
              audience.selectedGroupIds.length > 0) ||
            (audience.includedMemberIds &&
              audience.includedMemberIds.length > 0))
        ) {
          // Campaign has audience data - load it into Redux
          const state = store.getState();
          const latestGroups = state.groups || groups;
          dispatch(
            setAudienceData({
              ...audience,
              topicIds: audience.topicIds || [],
              selectedGroups: calcSelectedGroups(audience, latestGroups),
              selectedMembers: await calcSelectedMembers(audience),
              filterData: audience.filterData || {},
            })
          );
        } else {
          // No audience in campaign data - ensure Redux is empty and apply default name
          const state = store.getState();
          if (hasAudienceData(state.audience)) {
            dispatch(resetAudienceBuilder());
          }
          applyDefaultAudienceName();
        }

        markLoaded();
        hasInitializedRef.current = true;
        campaignIdRef.current = currentCampaignId;
      } finally {
        isInitializingRef.current = false;
      }
    };

    initializeAudience();
  }, [groupsLoaded, groups, location.pathname]);

  useEffect(() => {
    if (!groupsLoaded || groups.length === 0 || isInitializingRef.current)
      return;

    const audience = editor.getField('audience');

    if (!audience) return;

    const state = store.getState();
    const currentState = state.audience;
    if (!currentState.selectedGroups) return;

    // Recalculate if group IDs exist but group names are missing
    // Note: This checks if group IDs exist in audience but groups are NOT found in groups list
    // This means groups were removed or not loaded yet - skip recalculation in that case
    const hasMissingGroups =
      audience.selectedGroupIds?.some(id => !groups.find(g => g.id === id)) ||
      audience.excludedGroupIds?.some(id => !groups.find(g => g.id === id)) ||
      audience.requiredGroupIds?.some(id => !groups.find(g => g.id === id));

    // Only recalculate if we have all the groups we need (no missing groups)
    if (!hasMissingGroups) {
      const recalculatedGroups = calcSelectedGroups(audience, groups);
      if (
        JSON.stringify(recalculatedGroups) !==
        JSON.stringify(currentState.selectedGroups)
      ) {
        // Prevent loop by checking if we're already initializing
        if (isInitializingRef.current) return;
        isInitializingRef.current = true;

        dispatch(
          setAudienceData({
            ...currentState,
            topicIds: currentState.selectedTopicsList || [],
            selectedGroups: recalculatedGroups,
          })
        );

        // Reset flag after dispatch
        setTimeout(() => {
          isInitializingRef.current = false;
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, groupsLoaded]);

  return isLoading;
};

const AudienceEditor = () => {
  const editor = useEditor();
  const location = useLocation();
  const { id: campaignId } = useParams();

  // Get campaign ID from URL
  const queryParams = queryString.parse(location.search);

  // State for fetched audience and loading
  const [fetchedAudience, setFetchedAudience] = useState(null);
  const [isLoadingAudience, setIsLoadingAudience] = useState(true);

  // Get audience from editor params if passed directly (for local navigation)
  // Otherwise use fetched audience from API
  const activeAudience = editor.hasParam('audience')
    ? editor.getParam('audience')
    : null;
  const audienceToUse = activeAudience || fetchedAudience;

  // Fetch audience from API when campaignId is available
  // Always fetch when step=audience is in URL (after applying audience from SavedAudiencesPage)
  useEffect(() => {
    const isStepAudience = queryParams.step === 'audience';

    // Fetch if: step=audience in URL (force fetch) OR no audience loaded yet
    const shouldFetchAudience =
      !!campaignId && (isStepAudience || !audienceToUse);

    if (!shouldFetchAudience) {
      console.log('[AudienceEditor] Skipping fetch - setting loading to false');
      setIsLoadingAudience(false);
      return;
    }

    let isCancelled = false;

    const fetchAudience = async () => {
      try {
        const campaign = await fetchCampaign(campaignId);
        const audience = campaign?.audience || null;

        if (!isCancelled) {
          setFetchedAudience(audience);
          setIsLoadingAudience(false);
        } else {
          console.log('[AudienceEditor] Fetch cancelled, not setting state');
        }
      } catch (error) {
        if (!isCancelled) {
          setFetchedAudience(null);
          setIsLoadingAudience(false);
        }
      }
    };

    fetchAudience();

    return () => {
      isCancelled = true;
    };
  }, [campaignId, queryParams.step]);

  // Apply fetched audience to editor when available
  // If step=audience, always apply fetched audience (overwrite existing)
  useEffect(() => {
    const isStepAudience = queryParams.step === 'audience';

    // Apply if: fetched audience exists AND (step=audience OR editor doesn't have audience)
    if (fetchedAudience && (isStepAudience || !editor.getField('audience'))) {
      editor.setField('audience', fetchedAudience);
    }
  }, [fetchedAudience, queryParams.step]);

  const isLoading = useInitializeAudience();
  useFullScreenSpinner(
    'Loading audience data...',
    isLoading || isLoadingAudience
  );

  const { errorToast } = useToast();

  const goBack = () => {
    // Save happens automatically via onBeforeStepChange in CampaignEditor
    editor.goToStep('stages');
  };

  const audienceData = useSelector(audienceSelector);
  const isAudienceNotReady = !hasAudienceData(audienceData);
  const lastSyncedAudienceRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const isSyncingRef = useRef(false); // Prevent recursive sync loops

  // Sync Redux audience changes to campaign editor for autosave
  useEffect(() => {
    // Wait for initialization to complete before syncing
    if (isLoading) {
      return;
    }

    // Prevent recursive sync loops
    if (isSyncingRef.current) {
      return;
    }

    // On first load after initialization, store the initial state without syncing
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Store the initial audience data to compare against future changes
      const campaignAudienceData = {
        name: audienceData.name || '',
        description: audienceData.description || '',
        ...convertAudienceDataForDraft(audienceData),
      };
      lastSyncedAudienceRef.current = JSON.stringify(campaignAudienceData);
      return;
    }

    // Convert Redux audience data to campaign format
    const campaignAudienceData = {
      name: audienceData.name,
      description: audienceData.description,
      ...convertAudienceDataForDraft(audienceData),
    };

    // Check if audience data has changed by comparing serialized versions
    const currentAudienceString = JSON.stringify(campaignAudienceData);
    if (
      lastSyncedAudienceRef.current &&
      lastSyncedAudienceRef.current === currentAudienceString
    ) {
      return; // No changes, skip sync
    }

    // Also check if editor already has the same data to prevent loops
    const editorAudience = editor.getField('audience');
    if (editorAudience) {
      const editorAudienceString = JSON.stringify({
        name: editorAudience.name,
        description: editorAudience.description,
        topicIds: editorAudience.topicIds,
        selectedGroupIds: editorAudience.selectedGroupIds,
        requiredGroupIds: editorAudience.requiredGroupIds,
        excludedGroupIds: editorAudience.excludedGroupIds,
        includedMemberIds: editorAudience.includedMemberIds,
        excludedMemberIds: editorAudience.excludedMemberIds,
        filterData: editorAudience.filterData,
      });
      if (editorAudienceString === currentAudienceString) {
        // Editor already has this data, update ref but don't sync
        lastSyncedAudienceRef.current = currentAudienceString;
        return;
      }
    }

    // Sync to campaign editor (this triggers autosave via onDataChange)
    isSyncingRef.current = true;
    editor.setField('audience', campaignAudienceData);
    lastSyncedAudienceRef.current = currentAudienceString;

    // Reset sync flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isSyncingRef.current = false;
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Watch the entire audienceData object - Redux will create new references when state changes
    // Don't include 'editor' in deps - it's recreated on every render and causes infinite loops
    // The editor methods (setField, getField) are stable and can be called without being in deps
    audienceData,
    isLoading,
  ]);

  const handleConfirm = async () => {
    if (isAudienceNotReady) {
      errorToast(
        'Please select at least one topic, group, filter, or contact for your audience.',
        5
      );
      return;
    }

    editor.setField('audience', {
      name: audienceData.name,
      description: audienceData.description,
      ...convertAudienceDataForDraft(audienceData),
    });
    editor.goToStep('review-campaign');
  };

  return (
    <Step
      title="Audience"
      description="Select the Audience that your new Campaign should watch"
      onBack={goBack}
      nextText="Review"
      isNextDisabledVisual={isAudienceNotReady}
      onNext={handleConfirm}
      withoutHorizontalPaddingInBody
    >
      <Audience
        withoutPadding
        withoutTitle
        withoutPreview
        alignTitleToLeft
        centerTabs
        isStandaloneAudience
        disableAutosave
      />
    </Step>
  );
};

export default AudienceEditor;
